import {
  ChevronsUpDown, CircleUser, LogOut, Moon, Monitor, PanelLeftClose, PanelLeftOpen, PhoneCall, Sun,
} from "lucide-react";
import * as React from "react";
import {
  Link, NavLink, Outlet, useLocation, useMatch, useResolvedPath,
} from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator, Tooltip, TooltipProvider } from "@/components/ui/misc";
import { UserAvatar } from "@/features/user-avatar";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/feedback";
import { FrameTop, FrameTopContext, PageBackContext } from "@/components/layout/page";
import { PANE_HEAD, PANE_LIST_TOP, PANE_ROW, PANE_ROW_GAP } from "@/components/layout/pane-rows";
import { TourProvider } from "@/components/layout/tour";
import { useTheme } from "@/components/layout/theme";
import { useSoftphone } from "@/softphone/softphone-context";
import { TakingCallsStrip } from "@/softphone/taking-calls-strip";
import { SoftphoneDock } from "@/softphone/softphone-dock";
import { NotificationsHeaderButton, NotificationWatcher } from "@/features/notifications";
import { MessagesWatcher, useUnreadMessages } from "@/features/messages-watch";
import { SignedInAsBar } from "@/features/signed-in-as-bar";
import { useAuth } from "@/lib/auth";
import { LiveHints } from "@/lib/live-hints";
import {
  ACCOUNT_ITEM, SETTINGS_ITEM, SYSTEM_UPDATES_ITEM, hasSettings, isSettingsPath, settingsBackLink,
  visibleSections, type NavItem,
} from "@/lib/nav";
import { useStages, useUiPrefs } from "@/lib/queries";
import { hasUnseen, useSystemUpdates } from "@/lib/system-updates";
import { setStageCatalog } from "@/lib/stages";
import { useTasksAvailable } from "@/lib/tasks";
import { ROLE_LABELS } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Whether the sidebar is folded to its icons. This browser only, like the phone's keypad
 *  preference: how much room a screen can spare for the nav is a fact about the screen. */
const NAV_COLLAPSED_KEY = "acl.nav.collapsed";

function readNavCollapsed(): boolean {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function writeNavCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(NAV_COLLAPSED_KEY, String(collapsed));
  } catch {
    /* non-fatal — the sidebar simply opens expanded after a reload */
  }
}

export function AppShell() {
  const { user } = useAuth();
  const phone = useSoftphone();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(readNavCollapsed);
  // The tenant's stages (ADR-077 in `platform`). Every stage label and picker reads them, as they
  // render, from `lib/stages.ts`, so the registry is set here, before the page below renders, and
  // the page waits for the first answer rather than drawing the built-in names and then the
  // tenant's. A failure does not hold it up: the built-in thirteen answer while it retries.
  const stages = useStages();
  setStageCatalog(stages.data);
  const stagesSettled = !stages.isPending || stages.failureCount > 0;

  // Tasks shows once core-api says it has them (ACL-408): a console that reaches a server before
  // its core-api links to no page that cannot load.
  const tasksAvailable = useTasksAvailable();
  // Team Chat shows once core-api says it has them (platform ADR-186), with its unread count.
  const unreadMessages = useUnreadMessages();
  const sections = visibleSections(user?.role, [
    ...(tasksAvailable ? [] : ["/tasks"]),
    ...(unreadMessages === null ? ["/messages"] : []),
  ]).map((section) => ({
    ...section,
    items: section.items.map((item) =>
      item.to === "/messages" && unreadMessages ? { ...item, badge: String(unreadMessages) } : item,
    ),
  }));
  // The changelog (ACL-631), once core-api says it has one, marked when it holds an entry the
  // person has not opened the page since. Never the bell's: that is `NotificationWatcher`'s.
  const systemUpdates = useSystemUpdates();
  const uiPrefs = useUiPrefs();
  const systemUpdatesItem =
    systemUpdates.available === true
      ? hasUnseen(systemUpdates.data, uiPrefs.data)
        ? { ...SYSTEM_UPDATES_ITEM, badge: "New" }
        : SYSTEM_UPDATES_ITEM
      : null;
  // The pages behind Settings have no link of their own here, so they get one back over their title.
  const back = React.useMemo(
    () => settingsBackLink(user?.role, location.pathname),
    [user?.role, location.pathname],
  );
  const toggleNav = () => {
    writeNavCollapsed(!collapsed);
    setCollapsed(!collapsed);
  };
  // The frame's top pane, which a screen fills through `FrameTop` (`components/layout/page.tsx`).
  const [frameTop, setFrameTop] = React.useState<HTMLElement | null>(null);

  // One frame around the page: the sidebar, the top pane and the phone's rail are all on
  // `bg-frame`, with no line between any of them and the page, and the page is a sheet with
  // rounded top corners laid on it. They were three white panes ruled off from a page of the
  // same white, which read as four boxes.
  return (
    <TooltipProvider delayDuration={300}>
      <div className="app-frame flex h-dvh flex-col overflow-hidden bg-frame">
        {/* Only while an admin is signed in as somebody else (ADR-040 in `platform`). */}
        <SignedInAsBar />
        <div className="app-frame flex min-h-0 flex-1 overflow-hidden">
          <aside
            className={cn(
              "hidden shrink-0 flex-col overflow-hidden md:flex print:hidden",
              "transition-[width] duration-150",
              collapsed ? "w-14" : "w-56",
            )}
          >
            {/* The rules of this pane are short ones, set in from its edges (`.pane-rule-b` in
                `index.css`), as the one between two groups of the folded list is. Its head, the
                room under it and its folded rows are the sizes the phone's rail is built from
                too (`pane-rows.ts`), so the two columns of icons are level. */}
            <div
              className={cn(
                "pane-rule-b flex shrink-0 items-center gap-2",
                PANE_HEAD,
                collapsed ? "justify-center" : "pl-4 pr-2",
              )}
            >
              {!collapsed && (
                <>
                  <div className="flex size-6 shrink-0 items-center justify-center rounded bg-accent-9 text-white">
                    <PhoneCall className="size-3.5" aria-hidden />
                  </div>
                  <span className="min-w-0 flex-1 truncate whitespace-nowrap text-sm font-semibold tracking-tight">
                    AI Case Leads
                  </span>
                </>
              )}
              {/* One button, in one place in the tree for both widths, so keyboard focus stays on it
                  while the sidebar changes size around it. */}
              <Tooltip content={collapsed ? "Expand the sidebar" : "Collapse the sidebar"} side="right">
                <button
                  type="button"
                  onClick={toggleNav}
                  aria-expanded={!collapsed}
                  aria-label={collapsed ? "Expand the sidebar" : "Collapse the sidebar"}
                  className={cn(
                    "group flex shrink-0 items-center justify-center rounded-md text-text-muted transition-colors",
                    "hover:bg-frame-hover hover:text-text focus-visible:bg-frame-hover",
                    collapsed ? "size-8" : "size-7",
                  )}
                >
                  {collapsed ? (
                    <>
                      {/* Folded, the button is the logo until a pointer or the keyboard reaches it,
                          and then shows what clicking it does. */}
                      <span className="flex size-6 items-center justify-center rounded bg-accent-9 text-white group-hover:hidden group-focus-visible:hidden">
                        <PhoneCall className="size-3.5" aria-hidden />
                      </span>
                      <PanelLeftOpen
                        className="hidden size-4 group-hover:block group-focus-visible:block"
                        aria-hidden
                      />
                    </>
                  ) : (
                    <PanelLeftClose className="size-4" aria-hidden />
                  )}
                </button>
              </Tooltip>
            </div>

            <nav
              className={cn(
                "relative flex-1 overflow-y-auto overflow-x-hidden pb-4 scrollbar-subtle",
                PANE_LIST_TOP,
                collapsed ? "space-y-3 px-2" : "space-y-5 px-3",
              )}
            >
              {sections.map((section, index) => (
                <div key={section.heading}>
                  {collapsed ? (
                    // The headings need the width the sidebar gave up; a rule keeps the groups apart.
                    index > 0 && <div className="mx-2 mb-3 border-t border-border" aria-hidden />
                  ) : (
                    <p className="whitespace-nowrap px-2 pb-1.5 text-2xs font-medium uppercase tracking-wide text-text-subtle">
                      {section.heading}
                    </p>
                  )}
                  <ul className={cn("flex flex-col", PANE_ROW_GAP)}>
                    {section.items.map((item) => (
                      <li key={item.to}>
                        {item.subheading && !collapsed && (
                          <p className="whitespace-nowrap px-2 pb-1 pt-2 text-2xs text-text-subtle">
                            {item.subheading}
                          </p>
                        )}
                        <SidebarLink item={item} collapsed={collapsed} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>

            <div className="pane-rule-t shrink-0 space-y-1 p-2">
              {/* Everything set up once and changed now and then, one click down (`lib/nav.ts`). At
                  the foot rather than in the list, so it is in the same place however long the
                  list above it grows. Indented to line its icon up with the links above. */}
              {systemUpdatesItem && (
                <div className={cn(!collapsed && "px-1")}>
                  <SidebarLink item={systemUpdatesItem} collapsed={collapsed} />
                </div>
              )}
              {hasSettings(user?.role) && (
                <div className={cn(!collapsed && "px-1")}>
                  <SidebarLink
                    item={SETTINGS_ITEM}
                    collapsed={collapsed}
                    active={isSettingsPath(location.pathname)}
                  />
                </div>
              )}
              <UserMenu compact={collapsed} side={collapsed ? "right" : "top"} />
            </div>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            {/* The mobile header carries the identity and the menu the sidebar holds on desktop. */}
            <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border bg-surface px-4 md:hidden print:hidden">
              <span className="flex items-center gap-2 text-sm font-semibold">
                <span className="flex size-6 items-center justify-center rounded bg-accent-9 text-white">
                  <PhoneCall className="size-3.5" aria-hidden />
                </span>
                AI Case Leads
              </span>
              <div className="flex items-center gap-1">
                <NotificationsHeaderButton />
                <UserMenu compact />
              </div>
            </header>

            {/* The frame's top pane. Empty, it is a thin band of frame over the page, which is
                what shows the page's rounded corners; a screen with a strip of its own puts it
                here (`FrameTop`). */}
            <div ref={setFrameTop} className="shrink-0 md:empty:h-2 print:hidden" />

            <main
              // Reset the scroll position on navigation by keying on the path; otherwise moving
              // from a scrolled list to a short page lands you halfway down an empty screen.
              key={location.pathname}
              // `relative` makes this the containing block of anything absolutely positioned in a
              // screen — every `sr-only` label is. Without it those are placed against the page,
              // at their spot in a long table, and push the document past the frame: a second
              // scrollbar and white space under the sidebar (Endpoints, 2026-09-28).
              // The page is a sheet on the frame: the canvas, with its top corners rounded where
              // it meets the panes, and lifted off them by a shadow that is barely there
              // (`.sheet-lift` in `index.css`). It scrolls inside itself, so the corners stay put.
              className="app-frame relative min-h-0 flex-1 overflow-y-auto bg-canvas scrollbar-subtle md:rounded-t-2xl md:sheet-lift"
            >
              {stagesSettled ? (
                <PageBackContext.Provider value={back}>
                  <FrameTopContext.Provider value={frameTop}>
                    {/* Taking calls on every page (ACL-553). The Phone page fills the pane with
                        its own strip, which has the same control first. */}
                    {phone.available && location.pathname !== "/phone" && (
                      <FrameTop>
                        <TakingCallsStrip />
                      </FrameTop>
                    )}
                    {/* A screen registers its product tour from inside the outlet, and the tour
                        draws over the page from here. Inside `main`, which is keyed on the path,
                        so a tour never outlives the screen it belongs to. */}
                    <TourProvider>
                      <Outlet />
                    </TourProvider>
                  </FrameTopContext.Provider>
                </PageBackContext.Provider>
              ) : (
                <div className="flex h-32 items-center justify-center">
                  <Spinner />
                </div>
              )}
              {/* Below `md` the phone floats over the bottom-right corner, so every page gets room
                  to scroll its last row out from under it. Wider, the phone has a column of its
                  own and covers nothing. */}
              {phone.available && <div aria-hidden className="h-16 md:hidden" />}
            </main>
          </div>

          {/* Last in the row: from `md` up, the phone is the layout's right-hand column. */}
          <div className="contents print:hidden">
            <SoftphoneDock />
          </div>
          {/* Renders nothing: the one subscription to the refetch hints, while somebody is in. */}
          <LiveHints />
          {/* Renders nothing either: a toast for each reminder that comes, and the count in the
              tab's title (ACL-506). */}
          <NotificationWatcher />
          {/* And a toast for a message that arrives while the person is elsewhere (ADR-186). */}
          <MessagesWatcher />
        </div>
      </div>
    </TooltipProvider>
  );
}

/**
 * One link in the sidebar, at either width.
 *
 * The class name is worked out here and handed over as a STRING. Folded, the link sits inside a
 * tooltip, whose trigger is a Radix Slot, and a Slot joins its own class name with the child's as
 * text: `NavLink`'s function form (`className={({ isActive }) => …}`) came out the other side as
 * the function's source code. The folded rail then had no classes at all — icons pushed to the
 * left edge, rows taller than the rail was drawn for, and no mark on the page you were on.
 */
function SidebarLink({
  item, collapsed, active,
}: {
  item: NavItem;
  collapsed: boolean;
  /** Whether this is the page you are on, when that is more than its own path: Settings is marked
   *  on every page behind it. */
  active?: boolean;
}) {
  const path = useResolvedPath(item.to);
  // What `NavLink` matches on without `end`: this page, or anything under it.
  const matched = useMatch({ path: path.pathname, end: false }) !== null;
  const isActive = active ?? matched;

  return (
    <Tooltip
      content={
        collapsed
          ? item.badge
            ? `${item.fullLabel ?? item.label} · ${item.badge}`
            : (item.fullLabel ?? item.label)
          : null
      }
      side="right"
    >
      <Link
        to={item.to}
        aria-current={isActive ? "page" : undefined}
        aria-label={collapsed || item.fullLabel ? (item.fullLabel ?? item.label) : undefined}
        className={cn(
          "flex items-center rounded-md text-sm transition-colors",
          collapsed ? cn(PANE_ROW, "justify-center") : "gap-2.5 whitespace-nowrap px-2 py-1.5",
          isActive
            ? "bg-accent-3 font-medium text-accent-11"
            : "text-text-muted hover:bg-frame-hover hover:text-text",
        )}
      >
        <item.icon className="size-4 shrink-0" aria-hidden />
        {!collapsed && item.label}
        {!collapsed && item.badge && (
          <Badge tone="outline" size="sm" className="ml-auto">
            {item.badge}
          </Badge>
        )}
      </Link>
    </Tooltip>
  );
}

function UserMenu({
  compact = false, side,
}: {
  compact?: boolean;
  /** Where the menu opens. Defaults to below the avatar in the mobile header and above it in the
   *  sidebar; the folded sidebar opens it to the right, beside a column too narrow to hold it. */
  side?: "top" | "right" | "bottom";
}) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  if (!user) return null;

  const menuSide = side ?? (compact ? "bottom" : "top");
  const themes = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ] as const;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex w-full items-center gap-2 rounded-md p-1.5 text-left transition-colors",
          "hover:bg-frame-hover focus-visible:bg-frame-hover",
        )}
      >
        <UserAvatar user={user} />
        {!compact && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-text">
                {user.display_name || user.email}
              </span>
              <span className="block truncate text-2xs text-text-muted">
                {ROLE_LABELS[user.role]}
              </span>
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-text-subtle" aria-hidden />
          </>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={menuSide === "right" ? "end" : "start"}
        side={menuSide}
        className="w-56"
      >
        <DropdownMenuLabel>Signed in</DropdownMenuLabel>
        <div className="px-2 pb-2">
          <p className="truncate text-sm text-text">{user.display_name || user.email}</p>
          <p className="truncate text-xs text-text-muted">{user.email}</p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            <Badge tone="accent" size="sm">
              {ROLE_LABELS[user.role]}
            </Badge>
            {user.domain_name && (
              <Badge tone="outline" size="sm" className="font-mono">
                {user.domain_name}
              </Badge>
            )}
          </div>
        </div>
        <Separator />
        <DropdownMenuItem asChild>
          <NavLink to={ACCOUNT_ITEM.to}>
            <CircleUser aria-hidden />
            Account settings
          </NavLink>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        {themes.map((t) => (
          <DropdownMenuItem
            key={t.value}
            onSelect={(e) => {
              // Keep the menu open so the effect of each option can be compared without
              // reopening it three times.
              e.preventDefault();
              setTheme(t.value);
            }}
            className={cn(theme === t.value && "bg-surface-hover font-medium")}
          >
            <t.icon aria-hidden />
            {t.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={() => void logout()}>
          <LogOut aria-hidden />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
